import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";

// Components
import TextLoading from "../../../components/global/TextLoading";

// API Services
import {
  GetLastSequentialEnrollmentNumber,
  SetSequentialEnrollmentNumber,
} from "../../../api/StudentsReport";

const ModalConsecutiveEnrollment = ({ show, onHide, setConfirmEnabled }) => {
  // Translation hook
  const [t] = useTranslation(["global", "student"]);
  // State variables
  const [showModal, setShowModal] = useState(show);
  const [isLoading, setIsLoading] = useState(true);
  const [startingNumber, setStartingNumber] = useState(1);
  const [isEnabled, setIsEnabled] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastStudent, setLastStudent] = useState(null);
  const [hasStudent, setHasStudent] = useState(false);

  /* Initial Loading */
  useEffect(() => {
    getLastStudent();
  }, []);

  const getLastStudent = () => {
    setIsLoading(true);
    GetLastSequentialEnrollmentNumber()
      .then((response) => {
        if (response.status) {
          const data = response.data || {};
          const hasStudentName = Boolean(data.full_name);
          const lastStudentEnrollmentNumber = Number(data.enrollment) || 0;
          setLastStudent(data);
          setHasStudent(hasStudentName);
          setStartingNumber(lastStudentEnrollmentNumber + 1);
        }
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Handles the change of the starting number input
   * @param {number} value - The new value from the input field.
   */
  const changeStartingNumber = (value) => {
    const lastStudentEnrollmentNumber = Number(lastStudent?.enrollment) || 0;
    const isInvalid = isInvalidNumber(value);
    const numericValue = isInvalid
      ? lastStudentEnrollmentNumber + 1
      : Number(value);
    setStartingNumber(numericValue);
  };

  /**
   * Description: Validates if the input number is invalid.
   * @param {number} [number=1] - The number to validate.
   * @return {boolean}
   */
  const isInvalidNumber = (number = 1) => {
    const lastStudentEnrollmentNumber = Number(lastStudent?.enrollment) || 0;
    // Convert the input to a number
    const value = Number(number);
    // Check if the value is a safe integer and within the allowed range
    const maxSafeIntegerDB = Number.MAX_SAFE_INTEGER;
    const isSafeInteger = value < maxSafeIntegerDB;
    // Check if the value is not a number, less than 1, or exceeds the safe integer limit
    return (
      isNaN(value) ||
      value < 1 ||
      !isSafeInteger ||
      value <= lastStudentEnrollmentNumber
    );
  };

  /**
   * Description: Opens the confirmation modal and hides the current modal.
   */
  const openConfirmConsecutiveEnrollment = () => {
    setShowModal(false);
    setShowConfirmationModal(true);
  };

  /**
   * Description: Closes the confirmation modal and hides the main modal.
   */
  const closeConfirmationConsecutiveEnrollment = () => {
    setShowConfirmationModal(false);
    onHide();
  };

  /**
   * Description: Set the initial students consecutive enrollment number.
   */
  const confirmConsecutiveEnrollment = () => {
    const payload = {};

    payload.initial_enrollment_number = startingNumber - 1;

    setIsSaving(true);

    SetSequentialEnrollmentNumber(payload)
      .then((response) => {
        if (response) {
          setIsSaving(false);
          setConfirmEnabled();
          closeConfirmationConsecutiveEnrollment();
        }
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const lazyLoading = (
    <React.Fragment>
      {Array.from({ length: 4 }).map((_, index) => (
        <Row key={index} className="table-lazyLoading mt-2">
          <Col md={12}>
            <div />
          </Col>
        </Row>
      ))}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {/* Main Modal for Consecutive Enrollment Configuration */}
      <Modal id="consecutiveEnrollmentModal" show={showModal}>
        <Modal.Header>
          <Modal.Title id="consecutiveEnrollmentModalTitle">
            {t("student:modalConsecutiveEnrollment.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            lazyLoading
          ) : (
            <React.Fragment>
              {/* Description */}
              {hasStudent ? (
                <React.Fragment>
                  <Row className="mb-1">
                    <Col xs={12} className="mb-2">
                      <div id="assignAutomaticEnrollmentNumberDescription">
                        {t(
                          "student:modalConsecutiveEnrollment.assignAutomaticEnrollmentNumber",
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={12} className="mb-2">
                      <div id="lastStudentRegisteredDescription">
                        <Trans
                          i18nKey="student:modalConsecutiveEnrollment.lastStudentRegistered"
                          values={{
                            fullname: lastStudent.full_name?.toUpperCase(),
                            enrollmentNumber:
                              lastStudent.initial_enrollment_number ||
                              lastStudent.student_unique_id,
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col xs={12} className="mb-2">
                      <div id={"assignAutomaticEnrollmentNumberDescription2"}>
                        {t(
                          "student:modalConsecutiveEnrollment.assignAutomaticEnrollmentNumber2",
                        )}
                      </div>
                    </Col>
                  </Row>
                </React.Fragment>
              ) : (
                <Row className="mb-1">
                  <Col xs={12} className="mb-2">
                    <div id="consecutiveEnrollmentDescription">
                      {t("student:modalConsecutiveEnrollment.description")}
                    </div>
                  </Col>
                </Row>
              )}
              {/* Switch to assign consecutive enrollment */}
              <Row className="mb-1">
                <Col xs={12} className="mb-2">
                  <Form.Check
                    id="enableConsecutiveEnrollmentSwitch"
                    type="switch"
                    label={t("student:modalConsecutiveEnrollment.enable")}
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                  />
                </Col>
              </Row>
              {hasStudent && (
                <Row className="mb-1">
                  <Col xs={12} className="mb-2">
                    <div id="canAjustStartingNumberDescription">
                      {t(
                        "student:modalConsecutiveEnrollment.canAjustStartingNumber",
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              {/* Input starting number */}
              <Row className="mb-1">
                <Col xs={12} className="mb-2">
                  <Form.Group>
                    <Form.Label htmlFor="startingNumberInput">
                      {t("student:modalConsecutiveEnrollment.startingNumber")}
                    </Form.Label>
                    <Form.Control
                      id="startingNumberInput"
                      type="number"
                      disabled={!isEnabled}
                      value={startingNumber}
                      onChange={(e) => {
                        setStartingNumber(e.target.value);
                      }}
                      onBlur={(e) => changeStartingNumber(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="cancelConsecutiveEnrollmentButton"
            variant="secondary"
            onClick={onHide}
          >
            {t("global:buttons.cancel")}
          </Button>
          <Button
            id="saveConsecutiveEnrollmentButton"
            variant="primary"
            onClick={openConfirmConsecutiveEnrollment}
            disabled={!isEnabled}
          >
            {t("global:buttons.save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Consecutive Enrollment Modal */}
      <Modal
        show={showConfirmationModal}
        id="confirmConsecutiveEnrollmentModal"
      >
        <Modal.Header>
          <Modal.Title id="confirmConsecutiveEnrollmentModalTitle">
            {t("student:modalConsecutiveEnrollment.titleConfirm")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div id="confirmConsecutiveEnrollmentDescription1">
                {t("student:modalConsecutiveEnrollment.descriptionConfirm1")}
              </div>
              <div id="confirmConsecutiveEnrollmentDescription2">
                {t("student:modalConsecutiveEnrollment.descriptionConfirm2")}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="closeConfirmConsecutiveEnrollmentButton"
            variant="secondary"
            onClick={closeConfirmationConsecutiveEnrollment}
            disabled={isSaving}
          >
            {t("global:buttons.close")}
          </Button>
          <Button
            id="confirmConsecutiveEnrollmentButton"
            variant="primary"
            onClick={confirmConsecutiveEnrollment}
            disabled={isSaving}
          >
            {isSaving ? (
              <TextLoading
                text={t("global:buttons.activating")}
                variant="light"
              />
            ) : (
              t("global:buttons.confirm")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

ModalConsecutiveEnrollment.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  setConfirmEnabled: PropTypes.func,
};

ModalConsecutiveEnrollment.defaultProps = {
  setConfirmEnabled: () => {},
};

export default ModalConsecutiveEnrollment;
